import React, { useState, useCallback, useEffect } from 'react';
import { Box, Typography, Paper, Stack, IconButton, SxProps, Theme } from '@mui/material';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { AddAPhotoOutlined, Cancel, Close, VideocamOutlined } from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import { isEmpty } from '../../utils/lodash';
import ImageList from '@mui/material/ImageList';
import ImageListItem from '@mui/material/ImageListItem';
import CustomButton from '../Button/CustomButton';
import { RectangularImageCropperModal } from '../ImageCropper/RectangularImageCropperModal';
import CloseIcon from '../CustomSVGIcons/CloseIcon';

export type TImage = { type: 'image' | 'video'; src: string; rows: number; cols: number };

interface PhotoUploadModalProps {
    onChange: (items: TImage[], rawFiles?: File[]) => void;
    onCancelMediaUpload: () => void;
    initialValue?: TImage[];
    aspect?: number;
    supportedTypes?: ('image' | 'video')[];
    maxCount?: number;
    sx?: SxProps<Theme>;
}

const PhotoUploadModal: React.FC<PhotoUploadModalProps> = ({
    onChange,
    onCancelMediaUpload,
    initialValue,
    aspect,
    supportedTypes = ['image'],
    maxCount = 1,
    sx,
}) => {
    const { themeStyle } = useThemeContext();
    const [mediaItems, setMediaItems] = useState<TImage[]>([]);
    const [cropperOpen, setCropperOpen] = useState(false);
    const [src, setSrc] = useState<string>('');
    const [pendingFilesQueue, setPendingFilesQueue] = useState<File[]>([]);
    const [acceptedFiles, setAcceptedFiles] = useState<File[]>([]);
    const [processing, setProcessing] = useState(false);

    useEffect(() => {
        if (initialValue) setMediaItems(initialValue);
    }, [initialValue]);

    // Check if we can add more items
    const canAddMore = mediaItems.length < maxCount;

    // Get the appropriate icon based on supported types
    const getUploadIcon = () => {
        if (supportedTypes.includes('image') && supportedTypes.includes('video')) {
            return <AddAPhotoOutlined sx={{ fontSize: 24, color: theme[themeStyle].tag_white }} />;
        } else if (supportedTypes.includes('video')) {
            return <VideocamOutlined sx={{ fontSize: 24, color: theme[themeStyle].tag_white }} />;
        } else {
            return <AddAPhotoOutlined sx={{ fontSize: 24, color: theme[themeStyle].tag_white }} />;
        }
    };

    // Get the appropriate text based on supported types and count
    const getUploadText = () => {
        const isMultiple = maxCount > 1;
        const hasBothTypes = supportedTypes.includes('image') && supportedTypes.includes('video');

        if (hasBothTypes) {
            return isMultiple ? 'Add Photos/Videos' : 'Add Photo/Video';
        } else if (supportedTypes.includes('video')) {
            return isMultiple ? 'Add Videos' : 'Add Video';
        } else {
            return isMultiple ? 'Add Photos' : 'Add Photo';
        }
    };

    // Get the "Add More" button text
    const getAddMoreText = () => {
        const hasBothTypes = supportedTypes.includes('image') && supportedTypes.includes('video');

        if (hasBothTypes) {
            return 'Add More Video / Photo';
        } else if (supportedTypes.includes('video')) {
            return 'Add More Video';
        } else {
            return 'Add More Photo';
        }
    };

    // Function to calculate optimal grid layout based on image count
    const getGridLayout = (imageCount: number) => {
        if (imageCount === 1) {
            return { cols: 1, rowHeight: 200, gap: 0 };
        } else if (imageCount === 2) {
            return { cols: 2, rowHeight: 200, gap: 1 };
        } else if (imageCount === 3) {
            return { cols: 3, rowHeight: 130, gap: 1 };
        } else if (imageCount === 4) {
            return { cols: 2, rowHeight: 130, gap: 1 };
        } else if (imageCount === 5) {
            return { cols: 3, rowHeight: 130, gap: 1 };
        } else if (imageCount === 6) {
            return { cols: 3, rowHeight: 100, gap: 1 };
        } else if (imageCount <= 9) {
            return { cols: 3, rowHeight: 80, gap: 1 };
        } else {
            return { cols: 4, rowHeight: 80, gap: 1 };
        }
    };

    // Function to calculate item layout based on position and total count
    const getItemLayout = (index: number, totalCount: number) => {
        if (totalCount === 1) {
            return { cols: 1, rows: 1 };
        } else if (totalCount === 2) {
            return { cols: 1, rows: 1 };
        } else if (totalCount === 3) {
            return { cols: 1, rows: 1 };
        } else if (totalCount === 4) {
            return { cols: 1, rows: 1 };
        } else if (totalCount === 5) {
            // First 3 images get 1 col each, last 2 images get 1.5 cols each
            if (index < 3) {
                return { cols: 1, rows: 1 };
            } else {
                return { cols: 1.5, rows: 1 };
            }
        } else if (totalCount === 6) {
            return { cols: 1, rows: 1 };
        } else {
            return { cols: 1, rows: 1 };
        }
    };

    const onDrop = useCallback(
        (acceptedFiles: File[]) => {
            if (acceptedFiles.length > 0 && canAddMore) {
                setPendingFilesQueue(prev => [...prev, ...acceptedFiles]);
            }
        },
        [canAddMore],
    );

    // Build accept object based on supported types
    const getAcceptObject = () => {
        const accept: Record<string, string[]> = {};

        if (supportedTypes.includes('image')) {
            accept['image/png'] = [];
            accept['image/jpeg'] = [];
            accept['image/webp'] = [];
        }

        if (supportedTypes.includes('video')) {
            accept['video/*'] = [];
        }

        return accept;
    };

    const { getRootProps, getInputProps, open } = useDropzone({
        accept: getAcceptObject(),
        multiple: maxCount > 1,
        onDrop,
        disabled: !canAddMore,
    });

    const processNextFile = useCallback(() => {
        if (processing || cropperOpen || pendingFilesQueue.length === 0) return;
        setProcessing(true);
        const file = pendingFilesQueue[0];
        setSrc(URL.createObjectURL(file));
        setCropperOpen(true);
    }, [pendingFilesQueue, cropperOpen, processing]);

    const compressImage = (base64: string, quality = 0.7): Promise<string> => {
        return new Promise(resolve => {
            const img = new window.Image();
            img.onload = () => {
                const canvas = document.createElement('canvas');
                canvas.width = img.width;
                canvas.height = img.height;
                const ctx = canvas.getContext('2d');
                ctx?.drawImage(img, 0, 0);
                canvas.toBlob(
                    blob => {
                        if (blob) {
                            const reader = new FileReader();
                            reader.onloadend = () => {
                                resolve(reader.result as string);
                            };
                            reader.readAsDataURL(blob);
                        } else {
                            resolve(base64);
                        }
                    },
                    'image/jpeg',
                    quality,
                );
            };
            img.src = base64;
        });
    };

    const handleCropSave = async (base64: string, originalFile: File) => {
        setCropperOpen(false);
        setSrc('');

        // Determine file type
        const fileType = originalFile.type.startsWith('video/') ? 'video' : 'image';

        let processedSrc = base64;
        if (fileType === 'image') {
            processedSrc = await compressImage(base64, 0.7);
        }

        const newItem: TImage = { type: fileType, src: processedSrc, rows: 1, cols: 1 };

        setMediaItems(prevItems => {
            const updatedItems = [...prevItems, newItem];
            if (typeof onChange === 'function') {
                onChange(updatedItems, [...acceptedFiles, originalFile]);
            }
            return updatedItems;
        });

        setAcceptedFiles(prev => [...prev, originalFile]);

        setPendingFilesQueue(prev => {
            const nextQueue = prev.slice(1);
            setTimeout(() => {
                setProcessing(false);
                processNextFile();
            }, 100);
            return nextQueue;
        });
    };

    const handleCropCancel = () => {
        setCropperOpen(false);
        setSrc('');

        setPendingFilesQueue(prev => {
            const nextQueue = prev.slice(1);
            setTimeout(() => {
                setProcessing(false);
                processNextFile();
            }, 100);
            return nextQueue;
        });
    };

    const handleRemoveImage = (idx: number) => {
        const updatedMediaItems = mediaItems.filter((_, i) => i !== idx);
        const updatedAcceptedFiles = acceptedFiles.filter((_, i) => i !== idx);

        setMediaItems(updatedMediaItems);
        setAcceptedFiles(updatedAcceptedFiles);

        if (typeof onChange === 'function') {
            onChange(updatedMediaItems, updatedAcceptedFiles);
        }
    };

    useEffect(() => {
        if (!processing && !cropperOpen && pendingFilesQueue.length > 0) {
            processNextFile();
        }
    }, [pendingFilesQueue, processing, cropperOpen, processNextFile]);

    return (
        <Box
            sx={{
                width: '100%',
                flexShrink: 0,
                my: 2,
                border: `1px solid ${theme[themeStyle].tag_white}`,
                p: 0.5,
                borderRadius: 2,
                maxHeight: mediaItems.length <= 2 ? '220px' : '260px',
                position: 'relative',
                ...sx,
            }}>
            {acceptedFiles.length === 1 && (
                <Cancel
                    sx={{ fontSize: 32, cursor: 'pointer', position: 'absolute', right: 5, top: 5 }}
                    onClick={() => {
                        if (mediaItems.length === 1) {
                            setMediaItems([]);
                        }
                        if (acceptedFiles.length === 1) {
                            setAcceptedFiles([]);
                        }
                        onCancelMediaUpload();
                    }}
                />
            )}
            {isEmpty(mediaItems) ? (
                <Paper
                    elevation={0}
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: theme[themeStyle].deep_grey,
                        borderRadius: '8px',
                        padding: '20px',
                        textAlign: 'center',
                        height: '200px',
                        cursor: canAddMore ? 'pointer' : 'default',
                        opacity: canAddMore ? 1 : 0.6,
                    }}
                    {...getRootProps()}>
                    <input {...getInputProps()} />
                    <Box
                        sx={{
                            backgroundColor: theme[themeStyle].darkGray60,
                            borderRadius: '50%',
                            height: '40px',
                            width: '40px',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                        }}>
                        {getUploadIcon()}
                    </Box>
                    <Typography
                        sx={{
                            color: theme[themeStyle].tag_white,
                            fontSize: '14px',
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        }}>
                        {getUploadText()}
                    </Typography>
                    <Typography
                        sx={{
                            color: theme[themeStyle].tag_white,
                            fontSize: '10px',
                            marginBottom: '16px',
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        }}>
                        supports jpeg / png / webp
                    </Typography>
                    {maxCount > 1 && (
                        <Typography
                            sx={{
                                color: theme[themeStyle].tag_white,
                                fontSize: '10px',
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            }}>
                            Max {maxCount} items
                        </Typography>
                    )}
                </Paper>
            ) : (
                <Box
                    sx={{
                        maxWidth: '100%',
                        maxHeight: '260px',
                        overflowY: 'auto',
                        width: '100%',
                        minHeight: '200px',
                        position: 'relative',
                    }}>
                    <Stack
                        direction="row"
                        justifyContent={'space-between'}
                        sx={{
                            position: 'absolute',
                            width: '100%',
                            top: 0,
                            zIndex: 999,
                            pointerEvents: 'none',
                        }}>
                        <Box sx={{ pointerEvents: 'auto' }}>
                            {canAddMore && (
                                <CustomButton
                                    label={getAddMoreText()}
                                    onClick={open}
                                    withIcon
                                    beginningIcon={
                                        supportedTypes.includes('image') && supportedTypes.includes('video') ? (
                                            <AddAPhotoOutlined
                                                sx={{
                                                    fontSize: 18,
                                                    color: theme[themeStyle].true_white,
                                                    mr: 1,
                                                }}
                                            />
                                        ) : supportedTypes.includes('video') ? (
                                            <VideocamOutlined
                                                sx={{
                                                    fontSize: 18,
                                                    color: theme[themeStyle].true_white,
                                                    mr: 1,
                                                }}
                                            />
                                        ) : (
                                            <AddAPhotoOutlined
                                                sx={{
                                                    fontSize: 18,
                                                    color: theme[themeStyle].true_white,
                                                    mr: 1,
                                                }}
                                            />
                                        )
                                    }
                                    customStyle={{
                                        borderRadius: '4px',
                                    }}
                                />
                            )}
                        </Box>
                        {acceptedFiles.length === 1 && (
                            <Box sx={{ pointerEvents: 'auto' }}>
                                <CloseIcon
                                    stroke={theme[themeStyle].tag_white}
                                    onClick={() => {
                                        if (mediaItems.length === 1) {
                                            setMediaItems([]);
                                        }
                                        if (acceptedFiles.length === 1) {
                                            setAcceptedFiles([]);
                                        }
                                        onCancelMediaUpload();
                                    }}
                                    style={{ cursor: 'pointer' }}
                                />
                            </Box>
                        )}
                    </Stack>
                    <ImageList
                        variant="quilted"
                        cols={getGridLayout(mediaItems.length).cols}
                        gap={getGridLayout(mediaItems.length).gap}
                        rowHeight={getGridLayout(mediaItems.length).rowHeight}>
                        {mediaItems.map((item, index) => {
                            const itemLayout = getItemLayout(index, mediaItems.length);
                            return (
                                <ImageListItem key={index} cols={itemLayout.cols} rows={itemLayout.rows}>
                                    {item.type === 'video' ? (
                                        <video
                                            src={item.src}
                                            controls
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover',
                                                borderRadius: '1px',
                                                cursor: 'pointer',
                                            }}
                                        />
                                    ) : (
                                        <img
                                            src={item.src}
                                            alt={`media-${index}`}
                                            loading="lazy"
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover',
                                                borderRadius: '1px',
                                                cursor: 'pointer',
                                            }}
                                            onClick={() => window.open(item.src, '_blank')}
                                        />
                                    )}
                                    <IconButton
                                        size="small"
                                        sx={{
                                            position: 'absolute',
                                            top: 4,
                                            right: 4,
                                            background: 'rgba(255,255,255,0.7)',
                                        }}
                                        onClick={e => {
                                            e.stopPropagation();
                                            handleRemoveImage(index);
                                        }}>
                                        <Close fontSize="small" />
                                    </IconButton>
                                </ImageListItem>
                            );
                        })}
                    </ImageList>
                </Box>
            )}
            <RectangularImageCropperModal
                open={cropperOpen}
                src={src}
                onSave={handleCropSave}
                onCancel={handleCropCancel}
                aspect={aspect || 1}
            />
        </Box>
    );
};

export default PhotoUploadModal;
